<template>
  <a-modal
    :closable="!loading"
    :maskClosable="!loading"
    title="Alta al stock"
    v-model:visible="visible"
    @cancel="onCloseModal"
  >
    <a-typography-text>
      Alta al stock se utiliza para cargar colaboradores que ya están asegurados
      y no es necesario enviar a la compañía aseguradora o al colaborador para
      obtener sus datos.
      <br />
    </a-typography-text>

    <a-typography-title class="text-gray-8 mt-4" :level="5">
      Completa los siguientes datos
    </a-typography-title>

    <a-form
      class="mt-2"
      name="form"
      layout="vertical"
      ref="formRef"
      :model="formState"
      :rules="rules"
    >
      <a-form-item
        has-feedback
        name="insuranceCompany"
        ref="insuranceCompany"
        label="Aseguradora"
      >
        <a-select
          ref="select"
          :options="props.insuranceCompanies"
          v-model:value="formState.insuranceCompany"
          placeholder="Selecciona una aseguradora"
        />
      </a-form-item>

      <a-row type="flex" justify="space-between">
        <a-col :span="8">
          <a-form-item name="fileList">
            <a-upload
              name="avatar"
              :file-list="formState.fileList"
              :remove="handleRemoveFile"
              :before-upload="beforeUploadFile"
            >
              <a-button>
                <UploadOutlined />
                Adjuntar documentos
              </a-button>
            </a-upload>
          </a-form-item>
        </a-col>
        <a-col :span="8" :offset="8">
          <a-button
            v-if="formState.insuranceCompany"
            type="link"
            @click="downloadFormat"
          >
            Descargar formato
          </a-button>
        </a-col>
      </a-row>

      <a-alert
        v-if="previewLoading"
        class="mb-3"
        message="Revisando archivo..."
        type="info"
        show-icon
      />
      <a-alert
        v-else-if="previewError"
        class="mb-3"
        :message="previewError"
        type="error"
        show-icon
      />
      <a-alert
        v-else-if="preview"
        class="mb-3"
        message="Resumen del archivo"
        type="success"
        show-icon
      >
        <template #description>
          <div>Filas: {{ preview.rowCount }}</div>
          <div>
            Coberturas:
            <span v-if="preview.coverageColumns.length">
              {{ formatCoveragePreview(preview) }}
            </span>
            <span v-else>Sin columnas detectadas</span>
          </div>
        </template>
      </a-alert>

      <a-row>
        <a-form-item
          has-feedback
          name="sendWelcomeKit"
          ref="sendWelcomeKit"
          label="¿Enviar kit de bienvenida a los colaboradores?"
        >
          <a-radio-group v-model:value="formState.sendWelcomeKit">
            <a-radio :value="true">Sí</a-radio>
            <a-radio :value="false">No</a-radio>
          </a-radio-group>
        </a-form-item>
      </a-row>
    </a-form>

    <template #footer>
      <a-button :disabled="loading" @click="closeModal">Cancelar</a-button>
      <a-button type="primary" @click="uploadFile" :loading="loading">
        Confirmar
      </a-button>
    </template>
  </a-modal>
  <a-modal
    v-model:visible="showReport"
    title="Reporte de alta al stock"
    @cancel="onCloseReport"
    centered
    :maskClosable="false"
  >
    <a-row align="middle" :gutter="12">
      <a-col :span="3">
        <ExclamationCircleOutlined style="font-size: 36px; color: #faad14" />
      </a-col>
      <a-col :span="21">
        <a-typography-text>
          A continuación puedes descargar un reporte sobre las altas al stock
          realizadas y posibles problemas. La columna "Observación" muestra los
          detalles para cada registro.
        </a-typography-text>
      </a-col>
    </a-row>

    <template #footer>
      <a-button @click="onCloseReport" :loading="loadingReport">
        Cerrar
      </a-button>
      <a-button
        v-if="!loadingReport"
        type="primary"
        @click="downloadReport"
        :loading="loadingReport"
      >
        Descargar reporte
      </a-button>
    </template>
  </a-modal>
</template>

<script setup>
import { getUploadStockTemplate, getProcessedFile } from '@/network';
import {
  ref,
  reactive,
  watch,
  defineProps,
  defineExpose,
  defineEmits
} from 'vue';
import { message } from 'ant-design-vue';
import {
  UploadOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons-vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import {
  GET_LOGGED_USER,
  POST_INSURANCE_COMPANY,
  PREVIEW_STOCK_INCORPORATION
} from '../../store/types';
import notification from '../../utils/notifications';
import { formatCoveragePreview } from './stock-preview';

const props = defineProps({
  insuranceCompanies: {
    type: Array,
    default: []
  }
});

const emit = defineEmits(['uploaded']);

const store = useStore();
const route = useRoute();
const companyId = route.params.id;
const brokerId = store.getters[GET_LOGGED_USER].id;

const checkFiles = async () => {
  if (formState.fileList.length < 1 && formValidationType.value === 'upload') {
    return Promise.reject('Ingrese un archivo');
  }
  return Promise.resolve();
};

const handleRemoveFile = (file) => {
  resetPreview();
  if (file.id) {
    formState.fileList = [...formState.fileList, file.id];
  }
  const index = formState.fileList.indexOf(file);
  const newFileList = formState.fileList.slice();
  newFileList.splice(index, 1);
  formState.fileList = newFileList;
};

const preview = ref(null);
const previewLoading = ref(false);
const previewError = ref('');
const previewRequestId = ref(0);

const resetPreview = () => {
  previewRequestId.value += 1;
  preview.value = null;
  previewLoading.value = false;
  previewError.value = '';
};

const requestPreview = (file) => {
  resetPreview();
  const requestId = previewRequestId.value;
  if (!formState.insuranceCompany) {
    previewError.value = 'Seleccione una aseguradora para revisar el archivo';
    return;
  }

  const formData = new FormData();
  formData.append('insuranceCompanyId', formState.insuranceCompany);
  formData.append('companyId', companyId);
  formData.append('brokerId', brokerId);
  formData.append('sendWelcomeKit', String(formState.sendWelcomeKit === true));
  formData.append('file', file);

  previewLoading.value = true;
  store
    .dispatch(PREVIEW_STOCK_INCORPORATION, formData)
    .then((result) => {
      if (requestId === previewRequestId.value) preview.value = result;
    })
    .catch((error) => {
      if (requestId !== previewRequestId.value) return;
      previewError.value =
        error.response?.data?.message || 'No se pudo revisar el archivo';
    })
    .finally(() => {
      if (requestId === previewRequestId.value) {
        previewLoading.value = false;
      }
    });
};

const beforeUploadFile = (file) => {
  const fileSize = file.size / 1024 / 1024;
  if (fileSize > 5) {
    resetPreview();
    message.error('El archivo excede el peso máximo 5MB');
  } else {
    formState.fileList = [file];
    requestPreview(file);
  }
  return false;
};

const closeReportModal = () => {
  showReport.value = false;
};

const downloadReport = () => {
  try {
    const url = getProcessedFile(reportId.value);
    const reportLink = document.createElement('a');
    reportLink.href = url;
    reportLink.download = 'reporte.xlsx';
    reportLink.click();
    closeReportModal();
  } catch (e) {
    console.error('Error al formar la URL o descargar:', e);
  }
};

const openModal = () => {
  visible.value = true;
};

const onCloseModal = () => {
  formRef.value.resetFields();
  resetPreview();
};

const closeModal = () => {
  visible.value = false;
  onCloseModal();
};

const showReport = ref(false);
const loadingReport = ref(false);

const onCloseReport = () => {
  showReport.value = false;
};

const formValidationType = ref('upload'); // upload - download

const reportId = ref(null);

const loading = ref(false);
const uploadFile = () => {
  formValidationType.value = 'upload';
  formRef.value.validate().then(() => {
    loading.value = true;
    loadingReport.value = true;
    const formData = new FormData();
    formData.append('insuranceCompanyId', formState.insuranceCompany);
    formData.append('companyId', companyId);
    formData.append('brokerId', brokerId);
    formData.append('file', formState.fileList[0]);
    formData.append(
      'sendWelcomeKit',
      String(formState.sendWelcomeKit === true)
    );
    store
      .dispatch(POST_INSURANCE_COMPANY, formData)
      .then((response) => {
        notification(
          'success',
          'Correcto',
          'Alta al stock realizada con éxito.'
        );
        emit('uploaded', true);
        closeModal();
        reportId.value = response.data.id;
      })
      .catch((error) => {
        notification('error', 'Error', error.response.data.message);
      })
      .finally(() => {
        loading.value = false;
        loadingReport.value = false;
        showReport.value = true;
      });
  });
};

const downloadFormat = () => {
  if (!formState.insuranceCompany) {
    message.error('Seleccione una aseguradora');
    return;
  }
  formValidationType.value = 'download';
  const url = getUploadStockTemplate(companyId, formState.insuranceCompany);
  const link = document.createElement('a');
  link.href = url;
  link.click();
};

defineExpose({
  openModal,
  requestPreview
});

const visible = ref(false);
const formRef = ref();
const formState = reactive({
  insuranceCompany: '',
  fileList: [],
  sendWelcomeKit: null
});

watch(
  () => formState.insuranceCompany,
  (insuranceCompany) => {
    if (insuranceCompany && formState.fileList[0]) {
      requestPreview(formState.fileList[0]);
    }
  }
);
const rules = {
  insuranceCompany: [
    {
      required: true,
      message: 'Seleccione una aseguradora',
      trigger: 'change',
      type: 'number'
    }
  ],
  fileList: [
    {
      required: true,
      message: 'Ingrese un archivo',
      validator: checkFiles
    }
  ],
  sendWelcomeKit: [
    {
      required: true,
      message: 'Seleccione una opción',
      trigger: 'change',
      type: 'boolean'
    }
  ]
};
</script>
